import { cacheKeys } from "@constants/cache-keys";
import type { Category } from "@memorang/types";
import { useQuery } from "@tanstack/react-query";
import { updateUserProperties } from "analytics";
import { fetchExamsList } from "api/commerce/exam-service";
import { groupExamsByCategory } from "helpers/exams";
import { getPurchasedProducts } from "helpers/user";
import { useEffect, useMemo } from "react";
import useAuthUser from "./useAuthUser";
import { useGlobalStore } from "./useGlobalStore";

const useExams = () => {
	const { isSignedIn } = useAuthUser();

	const result = useQuery({
		queryKey: [cacheKeys.exams],
		queryFn: async () => {
			const exams = await fetchExamsList();
			return exams;
		},
		enabled: Boolean(isSignedIn),
	});

	const setIsPremiumUser = useGlobalStore((state) => state.setIsPremiumUser);
	const groupedExams = result.data ? groupExamsByCategory(result.data) : [];
	const examsList = useMemo(() => result.data ?? [], [result.data]);
	const purchasedProducts = useMemo(
		() => getPurchasedProducts(examsList),
		[examsList],
	);

	useEffect(() => {
		setIsPremiumUser(purchasedProducts.length > 0);
		if (purchasedProducts.length > 0) {
			updateUserProperties({
				premium_user: "true",
				products_purchased: JSON.stringify(
					purchasedProducts.map((product) => ({
						id: product.id,
						type: product.bundle.bundleType,
					})),
				),
			});
		}
	}, [purchasedProducts, setIsPremiumUser]);
	const categories = examsList
		.flatMap((exam) => exam.exam.category)
		.reduce((unique, category) => {
			return unique.find((item) => item.id === category.id)
				? unique
				: // biome-ignore lint/performance/noAccumulatingSpread: // TODO fix me later
					[...unique, category];
		}, [] as Category[]);

	return { groupedExams, examsList, categories, ...result };
};

export default useExams;
